<template>
    <section>

      <el-col :span="24">

        <section>

          <h5>General Remarks</h5>
          <!-- XOL-526 (S5, D9): the editor is now the honest write gate
               (canWriteOwnRequest() — Tower Co's own canWriteStep(), or
               isCounterpartyWriter() for a non-Tower-Co viewer) instead of
               the old `isTelco()`, which rendered a "live" editor for every
               Telco even though canWriteStep() (Tower-Co-only) silently
               discarded the save (D5). A Telco on a non-RTB request now
               correctly lands in the read-only branch below. -->
          <div v-if="canWriteOwnRequest() && !isTowerCo() && isActive(actionData) && !readOnly">
            <small v-if="generalRemarksNote" class="text-muted">{{generalRemarksNote}}</small>
            <html-input v-model="actionData.remarks" clsStyle="width: 52.8vw; overflow: auto;" :disabled="!canWriteOwnRequest() || readOnly" />
            <small v-if="generalRemarksStamp" class="text-muted">{{lastUpdatedLine(generalRemarksStamp)}}</small>
          </div>
          <div class="html-remarks" v-else>
            <p v-if="actionData.remarks" v-safe-html="actionData.remarks"></p>
            <!-- XOL-526 (UX U-H, step1.generalRemarks.empty): was "No client
                 remarks" — wrong for a non-Telco viewer of this read-only
                 branch. Since SPA-2/XOL-534, a backhaul provider on an active
                 RTB gets the editor above instead; this branch is for
                 everyone the canWriteOwnRequest()/isCounterpartyWriter() gate
                 still excludes. -->
            <p v-else class="text-muted">No general remarks</p>
            <small v-if="generalRemarksStamp" class="text-muted">{{lastUpdatedLine(generalRemarksStamp)}}</small>
          </div>

          <hr v-if="step.remarks" />

        </section>


        <el-alert
        v-if="this.isActive(this.actionData)"
        type="info"
        title="Progress Remarks"
        show-icon />

        <!-- XOL-526 (S5, D9): was `!canWriteStep()` — a counterparty may now
             write its own party-2 row (canWriteOwnStep()), which also folds
             in the party-1-step-completed lock (D9/A10). Tower Co unchanged. -->
        <small v-if="progressRemarksNote" class="text-muted">{{progressRemarksNote}}</small>
        <html-input v-model="step.remarks" :disabled="!canWriteOwnStep(step) || !isActive(actionData)" />
        <small v-if="progressRemarksStamp" class="text-muted">{{lastUpdatedLine(progressRemarksStamp)}}</small>

    </el-col>

  </el-col>

  </section>
</template>



<script>

import SiteMixins from '@/mixins/SiteMixins'

import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'

export default {

  // XOL-534 (reviewer): DateMixins (formatDateTime, used by lastUpdatedLine)
  // already arrives transitively via RequestMixins -> SiteMixins -> DateMixins
  // — a direct import here was redundant.
  mixins: [SiteMixins, RequestMixins],

  components: {
    FileDownload,
    FileUpload
  },


  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    }

  },


  methods: {

    // XOL-526: also captures the fresh Progress Remarks dirty baseline
    // (S5 "a fresh findStep on open") once this counterparty/Tower-Co-write
    // fetch resolves.
    setStep (data) {
      if (data) {
        this.step = data
        this.initialProgressRemarks = data.remarks
      }
    }

  },


  computed: {

    disabled () {
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly
    },

    // XOL-526 (UX §2.4, U-I): General Remarks is a single shared
    // `actions.remarks` column — Tower Co, the telco owner and the provider
    // all read the very same value, so its D12 stamp is shown to every
    // viewer, in either branch (unlike a per-party step row).
    generalRemarksStamp () {
      return this.editStamp(this.actionData.state, 'remarks')
    },

    generalRemarksNote () {
      return (this.isCounterpartyWriter() && this.isActive(this.actionData) && !this.readOnly && this.hasDistinctProvider())
        ? this.sharedFieldNote('edit these remarks')
        : null
    },

    // Progress Remarks is the counterparty's own party-2 STEP row — Tower
    // Co's own row (party 1) is a different object entirely and is never
    // shared, so it gets no note/stamp.
    progressRemarksStamp () {
      return (!this.isTowerCo() && this.step) ? this.editStamp(this.step.state, 'remarks') : null
    },

    progressRemarksNote () {
      return (this.canWriteOwnStep(this.step) && !this.isTowerCo() && this.isActive(this.actionData) && this.hasDistinctProvider())
        ? this.sharedFieldNote('edit these remarks')
        : null
    }

  },


  data () {

    return {
      actionData: this.rowData,
      step: this.stepData,

      // XOL-526 (S5): dirty baselines for the counterparty's own saves,
      // taken in created() (General Remarks) / setStep() (Progress Remarks,
      // once the fresh findStep() fetch resolves).
      initialGeneralRemarks: null,
      initialProgressRemarks: null
    }

  },  // data


  created () {

    // XOL-534 (reviewer R2): the baseline is set unconditionally, BEFORE the
    // possible findStep() fetch below — not only in the branch where the
    // fetch doesn't fire. A failed fetch (or the drawer closing before it
    // resolves) must never leave the baseline null, which would make the
    // load-time value look "dirty" and resend it, overwriting whatever the
    // OTHER counterparty saved in the meantime and re-stamping it as this
    // viewer's edit. setStep() still re-captures it fresh once the fetch
    // resolves (see the S5 "fresh findStep on open" mitigation).
    if (this.step) {
      this.initialProgressRemarks = this.step.remarks
    }

    // XOL-526 (S5, D9): was `canWriteStep()` — a counterparty may now write
    // its own party-2 row too.
    if ((this.canWriteStep() || this.canWriteOwnStep(this.step)) && !this.readOnly && this.step) {
      this.findStep(this.stepData.id, this.setStep)
    }

    if (this.isCounterpartyWriter()) {
      this.initialGeneralRemarks = this.actionData.remarks
    }

  },


  beforeDestroy () {

    this.$emit('updated', this.step)

    if (this.canWriteStep() && !this.readOnly && this.step) {
      this.saveStep(this.step)
      return
    }

    if (this.readOnly) return

    // XOL-526 (S5, D9): dirty-only, minimal-payload counterparty saves —
    // never the whole actionData/step object a Tower Co save resends.
    if (this.isCounterpartyWriter() && this.actionData.remarks !== this.initialGeneralRemarks) {
      this.saveOwnRequestFields({remarks: this.actionData.remarks})
    }

    if (this.canWriteOwnStep(this.step) && this.step && this.step.remarks !== this.initialProgressRemarks) {
      this.saveOwnStep(this.step, {remarks: this.step.remarks})
    }

  }

}

</script>
